import React, {useRef, useState, useEffect} from 'react'
import {DatePickerInput as PepsicoDsDatePicker} from 'pepsico-ds'
import {useDatePickerInput} from '@/components/atoms/DatePickerInput/datePickerInput-hook'
import {DATE_VALIDATION_MESSAGE} from '@/utils/constants/commonConstants'

/**
 * DatePickerInput component renders a date picker input field with validation and custom styling.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {string} props.value - The selected date value.
 * @param {function} props.updateValue - The function to update the selected date value.
 * @param {Object} props.uiSchema - The UI schema object for additional configurations.
 * @param {string} props.uiSchema.label - The label for the date picker input.
 * @param {string} [props.uiSchema.placeholder='mm/dd/yyyy'] - The placeholder text for the date picker input.
 * @param {boolean} [props.uiSchema.required=true] - Whether the date picker input is required.
 * @param {string} props.uiSchema.className - The CSS class name for the date picker input.
 * @param {string} [props.uiSchema.feedbackType=''] - The feedback type for the date picker input.
 * @param {string} [props.id=''] - Optional - The id for the date picker input.
 *
 * @returns {JSX.Element} The rendered DatePickerInput component.
 */
export default function DatePickerInput({value, updateValue, uiSchema, id = ''}) {
  const {
    label,
    placeholder = 'mm/dd/yyyy',
    required = true,
    className,
    feedbackType = '',
    isDark = false,
    validationRules = [],
    disabled = false,
    disabledHelperText = DATE_VALIDATION_MESSAGE.END_DATE_HELPER_TEXT,
    dependentDate,
  } = uiSchema

  const {validateDates} = useDatePickerInput(label, validationRules)
  const ref = useRef()
  const [isError, setIsError] = useState(false)
  const [helperText, setHelperText] = useState('')

  useEffect(() => {
    const lastElement = ref.current?.lastChild?.lastChild
    if (!lastElement) return

    const config = {attributes: true, childList: true, subtree: true}
    const callback = mutationList => {
      for (const mutation of mutationList) {
        if (mutation.type === 'attributes') {
          lastElement.style.display = lastElement.style.opacity === '0' ? 'none' : 'block'
        }
      }
    }
    const observer = new MutationObserver(callback)
    observer.observe(lastElement, config)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (dependentDate) {
      handleStartDateEndDateValidation(value)
    } else if (!value) {
      clearValidationError(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentDate, value])

  const handleStartDateEndDateValidation = val => {
    const errorMsg = validateDates(val)
    if (errorMsg) {
      handleValidationError(errorMsg, val)
    } else {
      clearValidationError(val)
    }
  }

  const handleValidationError = (errorMsg, val) => {
    setHelperText(errorMsg)
    updateValue(val, true)
    setIsError(true)
  }

  const clearValidationError = val => {
    updateValue(val, false)
    setIsError(false)
    setHelperText('')
  }

  const onChangeVal = val => {
    if (required && !val) {
      setIsError(true)
      setHelperText('Date is required')
      return
    }
    handleStartDateEndDateValidation(val)
  }

  return (
    <div ref={ref}>
      <PepsicoDsDatePicker
        id={`date_picker_input` + (id ? `_${id}` : '')}
        data-testid={`date_picker_input` + (id ? `_${id}` : '')}
        feedbackType={feedbackType}
        error={isError}
        helperText={disabled ? disabledHelperText : helperText}
        label={label}
        placeholder={placeholder} //Country wise date format
        required={required}
        selected={value}
        isDark={isDark}
        className={className}
        setSelected={val => {
          if (disabled) return
          onChangeVal(val)
        }}
        disabled={disabled}
      />
    </div>
  )
}
